import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SearchIcon,
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  RotateCcwIcon as RestoreIcon,
  LeafIcon,
  FlameIcon,
} from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { Multiselect } from "@/components/ui/multiselect";
import { useUnitMutations, UNIT_EMPTY_FORM, type UnitRow, type UnitFormState } from "./useUnitMutations";
import { UnitFormSheet } from "./UnitFormSheet";

const PAGE_SIZE = 25;

interface UnitTypeOption { id: string; code: string; name: string; deletedAt?: string | null }
type FilterStatus = "active" | "deleted" | "all";

interface Props {
  defaultSearch?: string;
}

export function UnitsTable({ defaultSearch = "" }: Props) {
  const { can } = usePermissions();

  const [search, setSearch]              = useState(defaultSearch);
  const [filterStatus, setFilterStatus]  = useState<FilterStatus>("active");
  const [filterTypeId, setFilterTypeId]  = useState("");
  const [page, setPage]                  = useState(1);
  const [sheetOpen, setSheetOpen]        = useState(false);
  const [deleteOpen, setDeleteOpen]      = useState(false);
  const [editing, setEditing]            = useState<UnitRow | null>(null);
  const [deleting, setDeleting]          = useState<UnitRow | null>(null);
  const [form, setForm]                  = useState<UnitFormState>({ ...UNIT_EMPTY_FORM });
  const [formError, setFormError]        = useState<string | null>(null);
  const [filterSgt, setFilterSgt]        = useState<"all" | "with" | "without">("all");
  const [filterOperatorIds, setFilterOperatorIds] = useState<string[]>([]);
  const [pageSize, setPageSize]          = useState(PAGE_SIZE);

  useEffect(() => {
    if (defaultSearch) {
      setSearch(defaultSearch);
      setPage(1);
    }
  }, [defaultSearch]);

  // Reset page when any filter changes
  useEffect(() => {
    setPage(1);
  }, [filterStatus, filterTypeId, search, filterSgt, filterOperatorIds, pageSize]);

  const { data: unitTypeOptions = [] } = useQuery({
    queryKey: ["unit-types-all"],
    queryFn: async () => {
      const res = await api["units"]["unit-types"].$get({ query: {} });
      if (!res.ok) return [];
      return res.json() as Promise<UnitTypeOption[]>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: operatorOptions = [] } = useQuery({
    queryKey: ["users-operators"],
    queryFn: async () => {
      const res = await api["users"].$get({ query: {} });
      if (!res.ok) return [];
      const all = await res.json() as { id: string; firstName: string; lastName: string; role: string }[];
      return all.filter(u => u.role === "operator" || u.role === "fleet_manager");
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: unitsResponse, isLoading } = useQuery({
    queryKey: ["units", filterStatus, filterTypeId, search, filterOperatorIds, page, pageSize],
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (filterStatus === "active")  query.deleted = "false";
      if (filterStatus === "deleted") query.deleted = "true";
      if (filterTypeId) query.unitTypeId = filterTypeId;
      if (search.trim()) query.search = search.trim();
      if (filterOperatorIds.length > 0) query.operatorIds = filterOperatorIds.join(",");
      query.page     = String(page);
      query.pageSize = String(pageSize);

      const res = await api["units"].$get({ query });
      if (!res.ok) throw new Error("Error loading units");
      return res.json() as Promise<{ data: UnitRow[]; total: number }>;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const unitsData   = unitsResponse?.data  ?? [];
  const serverTotal = unitsResponse?.total ?? 0;

  const filtered = useMemo(() => {
    return unitsData.filter((u) => {
      // search and operatorIds are handled server-side; only SGT filter remains client-side
      const hasSgt = (u.sgtCodes?.length ?? 0) > 0;
      if (filterSgt === "with"    && !hasSgt) return false;
      if (filterSgt === "without" &&  hasSgt) return false;
      return true;
    });
  }, [unitsData, filterSgt]);

  const totalPages = Math.max(1, Math.ceil(serverTotal / pageSize));
  const paged      = filtered;

  const { createMut, updateMut, deleteMut, restoreMut } = useUnitMutations(editing?.id ?? null, {
    onCreateSuccess: closeSheet,
    onUpdateSuccess: closeSheet,
    onDeleteSuccess: () => { setDeleteOpen(false); setDeleting(null); },
    onError: (err) => setFormError(err.message),
  });

  function closeSheet() {
    setSheetOpen(false);
    setEditing(null);
    setForm({ ...UNIT_EMPTY_FORM });
    setFormError(null);
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...UNIT_EMPTY_FORM, code: defaultSearch, licensePlate: defaultSearch });
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(u: UnitRow) {
    setEditing(u);
    setForm({
      code: u.code,
      name: u.name,
      unitTypeId: u.unitTypeId ?? "",
      licensePlate: u.licensePlate ?? "",
      vin: u.vin ?? "",
      brand: u.brand ?? "",
      model: u.model ?? "",
      engineNumber: u.engineNumber ?? "",
      manufactureYear: u.manufactureYear != null ? String(u.manufactureYear) : "",
      unitNumber: u.unitNumber != null ? String(u.unitNumber) : "",
      operatorId: u.operatorId ?? "",
      fuelType: u.fuelType ?? "",
      status: (u.status ?? "active") as import("./useUnitMutations").UnitStatus,
      hasSatellite: u.hasSatellite ?? false,
      location: u.location ?? "",
      observations: u.observations ?? "",
      // specs
      tare: u.specs?.tare != null ? String(u.specs.tare) : "",
      axles: u.specs?.axles ?? "",
      lengthM: u.specs?.lengthM != null ? String(u.specs.lengthM) : "",
      widthM: u.specs?.widthM != null ? String(u.specs.widthM) : "",
      heightM: u.specs?.heightM != null ? String(u.specs.heightM) : "",
      sealCount: u.specs?.sealCount != null ? String(u.specs.sealCount) : "",
      refrigerationUnitId: u.specs?.refrigerationUnitId ?? "",
      tempSensorCount: u.specs?.tempSensorCount != null ? String(u.specs.tempSensorCount) : "",
      isMapped: u.specs?.isMapped ?? false,
      isCalibrated: u.specs?.isCalibrated ?? false,
});
    setFormError(null);
    setSheetOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim() || !form.code.trim() || !form.unitTypeId) {
      setFormError("Código, nombre y tipo de unidad son requeridos");
      return;
    }
    const payload = { ...form, code: form.code.trim().toUpperCase(), name: form.name.trim() };
    if (editing) updateMut.mutate(payload);
    else createMut.mutate(payload);
  }

  const filterTypeName = unitTypeOptions.find((t) => t.id === filterTypeId);

  // Operators come from the users API (not derived from current page data)
  const uniqueOperators = useMemo(() => {
    return operatorOptions
      .map(u => [u.id, `${u.firstName} ${u.lastName}`] as [string, string])
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [operatorOptions]);

  return (
    <div className="space-y-4 w-full">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código, nombre, patente u operador..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <Select value={filterTypeId || "__all__"} onValueChange={(v) => setFilterTypeId((v ?? "") === "__all__" ? "" : (v ?? ""))}>
          <SelectTrigger className="w-44" size="sm">
            <SelectValue>
              {filterTypeName ? `${filterTypeName.code} — ${filterTypeName.name}` : "Todos los tipos"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los tipos</SelectItem>
            {unitTypeOptions.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.code} — {t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterSgt} onValueChange={(v: any) => setFilterSgt(v)}>
          <SelectTrigger className="w-36" size="sm">
            <SelectValue>
              {filterSgt === "all" ? "Todos (SGT)" : filterSgt === "with" ? "Con SGT" : "Sin SGT"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos (SGT)</SelectItem>
            <SelectItem value="with">Con SGT</SelectItem>
            <SelectItem value="without">Sin SGT</SelectItem>
          </SelectContent>
        </Select>

        <Multiselect
          options={uniqueOperators.map(([id, name]) => ({ value: id, label: name }))}
          values={filterOperatorIds}
          onValuesChange={setFilterOperatorIds}
          placeholder="Todos los operadores"
          searchPlaceholder="Buscar operador..."
          className="w-56"
        />

        <Select value={filterStatus} onValueChange={(v: any) => setFilterStatus(v)}>
          <SelectTrigger className="w-36" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Activas</SelectItem>
            <SelectItem value="deleted">Eliminadas</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
          </SelectContent>
        </Select>

        {can("write", "units") && (
          <Button onClick={openCreate} className="gap-2" size="sm">
            <PlusIcon className="size-4" /> Nueva unidad
          </Button>
        )}
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">Código</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead className="w-48">Tipo</TableHead>
              <TableHead className="w-16 text-center">N°</TableHead>
              <TableHead className="w-44">Marca / Modelo</TableHead>
              <TableHead className="w-36">Operador</TableHead>
              <TableHead className="w-28 text-center">Estado</TableHead>
              <TableHead className="w-12 text-center">SAT</TableHead>
              {can("write", "units") && <TableHead className="w-32 text-right pr-4">Acciones</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={can("write", "units") ? 9 : 8} className="text-center py-16 text-muted-foreground">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={can("write", "units") ? 9 : 8} className="text-center py-16 text-muted-foreground">
                  {search ? "Sin resultados para la búsqueda" : "No hay unidades"}
                </TableCell>
              </TableRow>
            ) : (
              paged.map((u) => (
                <TableRow key={u.id} className={u.deletedAt ? "opacity-70 italic" : undefined}>
                  <TableCell>
                    <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded tracking-wide">
                      {u.code}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium text-sm">
                    <div className="flex items-center gap-2">
                      <span>{u.name}</span>
                      {u.inMobile && (
                        <span
                          className="text-[10px] px-1.5 py-0 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 shrink-0"
                          title="Asignada a un móvil activo"
                        >
                          móvil
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded uppercase shrink-0">
                        {u.unitTypeCode}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">{u.unitTypeName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {u.unitNumber != null
                      ? <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{u.unitNumber}</span>
                      : <span className="text-muted-foreground text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <span>{[u.brand, u.model].filter(Boolean).join(" ") || "—"}</span>
                      {u.fuelType === "gnc" && (
                        <span title="GNC"><LeafIcon className="size-3 text-emerald-500" /></span>
                      )}
                      {u.fuelType === "diesel" && (
                        <span title="Diesel"><FlameIcon className="size-3 text-red-400" /></span>
                      )}
                      {u.specs?.refrigerationUnitId && (
                        <span className="text-[10px] px-1.5 py-0 bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 rounded font-medium shrink-0">❄ frio</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {u.operatorName ?? "—"}
                  </TableCell>

                  <TableCell className="text-center">
                    <span className={`text-[10px] px-1.5 py-0 rounded font-medium ${
                      u.status === "active"       ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                      u.status === "in_repair"    ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" :
                      u.status === "damaged"      ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" :
                                                   "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                    }`}>
                      {u.status === "active"       ? "Activo" :
                       u.status === "in_repair"    ? "Reparación" :
                       u.status === "damaged"      ? "Siniestrada" :
                                                    "Sin docs"}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {u.hasSatellite ? <span title="Satelital instalado">🛰️</span> : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </TableCell>

                  {can("write", "units") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        {u.deletedAt ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => restoreMut.mutate(u.id)}
                            disabled={restoreMut.isPending}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restaurar"
                          >
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(u)} title="Editar">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => { setDeleting(u); setDeleteOpen(true); }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Eliminar"
                            >
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      <UnitFormSheet
        open={sheetOpen}
        editing={editing}
        form={form}
        formError={formError}
        isSaving={createMut.isPending || updateMut.isPending}
        onFormChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        onSubmit={handleSubmit}
        onClose={closeSheet}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar unidad</DialogTitle>
            <DialogDescription>
              ¿Eliminás la unidad <strong>{deleting?.code}</strong> — {deleting?.name}? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
